import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

import { fetchCurrentUser, login, verifyMfa } from "@/api/endpoints/auth";
import { useAuthStore } from "@/store/authStore";

const credentialsSchema = z.object({
  username: z.string().min(1, "Required"),
  password: z.string().min(1, "Required"),
});
type Credentials = z.infer<typeof credentialsSchema>;

const totpSchema = z.object({
  code: z.string().length(6, "Enter the 6-digit code"),
});
type Totp = z.infer<typeof totpSchema>;

export function LoginPage() {
  const navigate = useNavigate();
  const setAccessToken = useAuthStore((s) => s.setAccessToken);
  const setSession = useAuthStore((s) => s.setSession);
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const credentialsForm = useForm<Credentials>({ resolver: zodResolver(credentialsSchema) });
  const totpForm = useForm<Totp>({ resolver: zodResolver(totpSchema) });

  async function completeLogin(accessToken: string) {
    // Put the token in the store first so apiFetch (used by fetchCurrentUser)
    // picks it up when attaching the Authorization header.
    setAccessToken(accessToken);
    const user = await fetchCurrentUser();
    setSession(accessToken, user);
    navigate("/dashboard", { replace: true });
  }

  const onSubmitCredentials = credentialsForm.handleSubmit(async ({ username, password }) => {
    setError(null);
    try {
      const result = await login(username, password);
      if (result.mfa_required && result.mfa_token) {
        setMfaToken(result.mfa_token);
      } else if (result.access_token) {
        await completeLogin(result.access_token);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Login failed");
    }
  });

  const onSubmitTotp = totpForm.handleSubmit(async ({ code }) => {
    if (!mfaToken) return;
    setError(null);
    try {
      const result = await verifyMfa(mfaToken, code);
      if (result.access_token) await completeLogin(result.access_token);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Invalid code");
    }
  });

  return (
    <div className="flex h-full items-center justify-center bg-bg">
      <div className="w-full max-w-sm rounded-lg border border-border bg-surface p-8">
        <p className="mb-1 font-mono text-xs uppercase tracking-wide text-accent">
          radius.deutschlandbvik.com
        </p>
        <h1 className="mb-6 text-xl font-semibold text-text">RADIUS Admin Portal</h1>

        {!mfaToken ? (
          <form onSubmit={onSubmitCredentials} className="flex flex-col gap-4">
            <label className="flex flex-col gap-1 text-sm text-text-muted">
              Username
              <input
                className="rounded-md border border-border bg-surface-2 px-3 py-2 text-text outline-none focus:border-accent"
                autoComplete="username"
                {...credentialsForm.register("username")}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-text-muted">
              Password
              <input
                type="password"
                className="rounded-md border border-border bg-surface-2 px-3 py-2 text-text outline-none focus:border-accent"
                autoComplete="current-password"
                {...credentialsForm.register("password")}
              />
            </label>
            {error && <p className="text-sm text-critical">{error}</p>}
            <button
              type="submit"
              disabled={credentialsForm.formState.isSubmitting}
              className="mt-2 rounded-md bg-accent px-4 py-2 text-sm font-medium text-bg disabled:opacity-60"
            >
              Sign in
            </button>
          </form>
        ) : (
          <form onSubmit={onSubmitTotp} className="flex flex-col gap-4">
            <label className="flex flex-col gap-1 text-sm text-text-muted">
              Authenticator code
              <input
                inputMode="numeric"
                maxLength={6}
                autoFocus
                className="rounded-md border border-border bg-surface-2 px-3 py-2 tracking-[0.3em] text-text outline-none focus:border-accent"
                {...totpForm.register("code")}
              />
            </label>
            {error && <p className="text-sm text-critical">{error}</p>}
            <button
              type="submit"
              disabled={totpForm.formState.isSubmitting}
              className="mt-2 rounded-md bg-accent px-4 py-2 text-sm font-medium text-bg disabled:opacity-60"
            >
              Verify
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
